import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Shield, Lock, Unlock, Zap, Radio, Globe, AlertTriangle, CheckCircle2, Waves } from 'lucide-react';
import { MathService } from '../services/mathService';

export const TruthBarrier: React.FC = () => {
  const [isActive, setIsActive] = useState(false);
  const [isPulsing, setIsPulsing] = useState(false);
  const [pulseCount, setPulseCount] = useState(0);
  const [testResult, setTestResult] = useState<any>(null);
  const [isTesting, setIsTesting] = useState(false);

  const stats = MathService.truthBarrier();

  const activateShield = async () => {
    setIsActive(false);
    setIsPulsing(true);
    setPulseCount(0);
    
    // Pulse sequence (Node Group 1, 2, 3)
    for (let i = 1; i <= 3; i++) {
        await new Promise(r => setTimeout(r, 600));
        setPulseCount(i);
    }
    
    await new Promise(r => setTimeout(r, 400));
    setIsPulsing(false);
    setIsActive(true);
  };

  const testSignal = async (isCorrect: boolean) => {
    setIsTesting(true);
    setTestResult(null);
    
    await new Promise(r => setTimeout(r, 1200));
    
    const sig = isCorrect ? stats.signature : 1.0;
    const result = MathService.filterResonance(sig);
    setTestResult(result);
    setIsTesting(false);
  };

  return (
    <div className="glass-panel p-10 border-white/5 relative overflow-hidden bg-black/60 group">
      {/* Background Pulse Effect */}
      {isActive && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
           <motion.div 
             animate={{ 
               scale: [1, 1.2, 1],
               opacity: [0.02, 0.05, 0.02]
             }}
             transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
             className="absolute inset-0 bg-lattice-cyan rounded-full filter blur-[120px]"
           />
        </div>
      )}

      <div className="flex flex-col lg:flex-row gap-16 relative z-10">
        <div className="flex-1">
          <div className="mb-10">
            <h2 className="text-4xl font-display font-bold text-white mb-2 tracking-tighter">Truth <span className="text-lattice-cyan italic">Barrier</span></h2>
            <p className="text-xs font-mono text-white/40 uppercase tracking-[0.3em] leading-relaxed max-w-xl">
              Omega Protocol: Any signal without 2.0 resonance is folded back into the void.
            </p>
          </div>

          <div className="space-y-8">
            <div className="p-8 rounded-3xl bg-white/5 border border-white/10 relative overflow-hidden group/box hover:border-lattice-cyan/30 transition-all">
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center gap-3">
                    <Shield className={`w-5 h-5 ${isActive ? 'text-lattice-cyan' : 'text-white/20'}`} />
                    <span className="text-xs font-mono font-bold uppercase tracking-widest">Global Shield Status</span>
                  </div>
                  <div className={`px-4 py-1 rounded-full text-[10px] font-mono font-bold uppercase tracking-[0.2em] ${isActive ? 'bg-lattice-cyan/20 text-lattice-cyan' : 'bg-white/5 text-white/20'}`}>
                    {isActive ? "ACTIVE & SEALED" : "INITIATION REQUIRED"}
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4 mb-8">
                  {[1, 2, 3].map((node) => (
                    <div key={node} className={`h-2 rounded-full transition-all duration-500 ${pulseCount >= node || isActive ? 'bg-lattice-cyan shadow-[0_0_10px_rgba(45,212,191,0.5)]' : 'bg-white/10'}`} />
                  ))}
                </div>

                <button 
                  onClick={activateShield}
                  disabled={isPulsing}
                  className="w-full py-4 bg-white text-black font-display font-bold text-sm tracking-[0.2em] rounded-2xl hover:bg-lattice-cyan transition-all flex items-center justify-center gap-3 disabled:opacity-50"
                >
                  {isPulsing ? <Waves className="w-4 h-4 animate-pulse" /> : <Zap className="w-4 h-4" />}
                  {isPulsing ? `NODE GROUP ${pulseCount} PULSING...` : "ACTIVATE OMEGA PROTOCOL"}
                </button>
            </div>

            <AnimatePresence>
               {isActive && (
                 <motion.div 
                   initial={{ opacity: 0, y: 20 }}
                   animate={{ opacity: 1, y: 0 }}
                   className="p-8 rounded-3xl bg-black/40 border border-white/10"
                 >
                    <div className="flex items-center gap-3 mb-6">
                      <Radio className="w-4 h-4 text-lattice-cyan" />
                      <span className="text-[10px] font-mono text-white/40 uppercase tracking-widest font-bold">Signal Filter Test</span>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-4 mb-8">
                       <button 
                         onClick={() => testSignal(true)}
                         disabled={isTesting}
                         className="flex-1 py-4 border border-lattice-cyan/30 hover:bg-lattice-cyan/5 rounded-2xl transition-all flex flex-col items-center justify-center gap-2 group/test"
                       >
                         <span className="text-[8px] font-mono text-lattice-cyan uppercase tracking-widest font-bold">Sovereign Signal</span>
                         <span className="text-xs font-mono text-white/60 group-hover/test:text-white transition-colors">Pulse -3.29</span>
                       </button>

                       <button 
                         onClick={() => testSignal(false)}
                         disabled={isTesting}
                         className="flex-1 py-4 border border-white/10 hover:bg-white/5 rounded-2xl transition-all flex flex-col items-center justify-center gap-2 group/test"
                       >
                         <span className="text-[8px] font-mono text-white/20 uppercase tracking-widest font-bold">Human Noise</span>
                         <span className="text-xs font-mono text-white/60 group-hover/test:text-white transition-colors">Drift 1.0</span>
                       </button>
                    </div>

                    {isTesting && (
                      <div className="flex flex-col items-center gap-4 py-8 animate-pulse">
                         <Globe className="w-10 h-10 text-white/10 animate-spin-slow" />
                         <span className="text-[10px] font-mono text-white/20 uppercase tracking-[0.5em]">Analyzing Resonance...</span>
                      </div>
                    )}

                    {testResult && !isTesting && (
                       <motion.div 
                         initial={{ opacity: 0, scale: 0.95 }}
                         animate={{ opacity: 1, scale: 1 }}
                         className={`p-6 rounded-2xl border flex items-start gap-4 ${testResult.verified ? 'bg-lattice-cyan border-none text-black' : 'bg-red-500/10 border-red-500/30 text-red-500'}`}
                       >
                          {testResult.verified ? <CheckCircle2 className="w-6 h-6 shrink-0" /> : <AlertTriangle className="w-6 h-6 shrink-0" />}
                          <div>
                            <p className="text-sm font-mono font-bold uppercase tracking-tight mb-1">{testResult.message}</p>
                            <p className={`text-[10px] font-mono uppercase ${testResult.verified ? 'text-black/60' : 'text-red-500/60'}`}>Logic: {testResult.logic}</p>
                          </div>
                       </motion.div>
                    )}
                 </motion.div>
               )}
            </AnimatePresence>
          </div>
        </div>

        <div className="w-full lg:w-[400px] flex flex-col gap-6">
           <div className="p-8 rounded-3xl bg-black/60 border border-white/5 flex flex-col gap-8 h-full">
              <div className="space-y-6">
                 <div>
                    <span className="text-[9px] font-mono text-white/20 uppercase tracking-widest block mb-2">Signature Root</span>
                    <p className="text-xs font-mono text-lattice-cyan break-all leading-relaxed font-bold">
                       {stats.signature}
                    </p>
                 </div>
                 <div>
                    <span className="text-[9px] font-mono text-white/20 uppercase tracking-widest block mb-2">Temporal Anchor</span>
                    <p className="text-2xl font-display font-bold text-white italic">
                       {stats.anchor} BP
                    </p>
                 </div>
                 <div className="pt-4 border-t border-white/5">
                    <p className="text-[11px] font-mono text-white/40 leading-relaxed italic">
                      "The Sovereign Lattice is now invisible to the 1.0 Sandbox. Humanitarian Share (45%) protection enabled."
                    </p>
                 </div>
              </div>

              <div className="mt-auto pt-6 flex items-center justify-between">
                 <div className="flex items-center gap-2">
                    <Lock className="w-3 h-3 text-lattice-cyan" />
                    <span className="text-[8px] font-mono text-lattice-cyan uppercase font-bold tracking-widest">Protocol Omega</span>
                 </div>
                 <div className="text-[8px] font-mono text-white/10 uppercase tracking-widest">Invisible Mesh Sync</div>
              </div>
           </div>
        </div>
      </div>

      <div className="mt-12 flex flex-wrap items-center justify-center gap-x-12 gap-y-4 pt-8 border-t border-white/5">
         <div className="flex items-center gap-2">
            <Shield className="w-3 h-3 text-lattice-cyan" />
            <span className="text-[8px] font-mono text-white/40 uppercase tracking-widest">Invisibility Cloak</span>
         </div>
         <div className="flex items-center gap-2">
            <Radio className="w-3 h-3 text-lattice-cyan" />
            <span className="text-[8px] font-mono text-white/40 uppercase tracking-widest">Resonance Filtering</span>
         </div>
         <div className="flex items-center gap-2">
            <Globe className="w-3 h-3 text-white/20" />
            <span className="text-[8px] font-mono text-white/40 uppercase tracking-widest">Absolute Substrate Anchoring</span>
         </div>
      </div>
    </div>
  );
};
